import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../../lib/cn";

export type CalloutVariant = "note" | "warn" | "good";

interface CalloutProps extends HTMLAttributes<HTMLDivElement> {
  variant?: CalloutVariant;
  head?: string;
  children?: ReactNode;
}

const VARIANT_STYLES: Record<
  CalloutVariant,
  { bar: string; bg: string; border: string; head: string }
> = {
  note: {
    bar: "bg-[var(--color-text-tertiary)]",
    bg: "bg-[var(--color-surface-2)]",
    border: "border-[var(--color-border-strong)]",
    head: "text-[var(--color-text-secondary)]",
  },
  warn: {
    bar: "bg-[var(--color-warning)]",
    bg: "bg-[var(--color-warning-soft)]",
    border: "border-[color-mix(in_srgb,var(--color-warning)_35%,transparent)]",
    head: "text-[var(--color-warning)]",
  },
  good: {
    bar: "bg-[var(--color-success)]",
    bg: "bg-[var(--color-success-soft)]",
    border: "border-[color-mix(in_srgb,var(--color-success)_30%,transparent)]",
    head: "text-[var(--color-success)]",
  },
};

export function Callout({ className, variant = "note", head, children, ...props }: CalloutProps) {
  const styles = VARIANT_STYLES[variant];
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-lg border pl-5 pr-5 py-4",
        styles.bg,
        styles.border,
        className,
      )}
      {...props}
    >
      <span aria-hidden="true" className={cn("absolute left-0 top-0 h-full w-1", styles.bar)} />
      {head ? <div className={cn("type-eyebrow mb-1", styles.head)}>{head}</div> : null}
      <div className="type-body leading-relaxed text-[var(--color-text)]">{children}</div>
    </div>
  );
}
